/**
 * Approval API Client
 * 
 * Helper functions for communicating with the approval workflow backend.
 */
import { BACKEND_URL } from '@/lib/backendUrl';


/**
 * Get pending approvals for a hotel
 * @param {string} hotelId - Hotel identifier
 * @param {string} role - Optional role filter (manager, owner)
 * @returns {Promise<Array>} List of pending approval summaries
 */
export async function getPendingApprovals(hotelId, role = null) {
  const url = new URL(`${BACKEND_URL}/api/blockchain/approvals/${hotelId}/pending`);
  if (role) {
    url.searchParams.append('role', role);
  }
  
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch pending approvals: ${response.statusText}`);
  }
  
  return await response.json();
}

/**
 * Get detailed information about a specific approval
 * @param {string} approvalId - Approval request ID
 * @returns {Promise<Object>} Full approval request details
 */
export async function getApprovalDetails(approvalId) {
  const response = await fetch(`${BACKEND_URL}/api/blockchain/approvals/${approvalId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch approval details: ${response.statusText}`);
  }
  
  return await response.json();
}

/**
 * Approve a pending request
 * @param {string} approvalId - Approval request ID
 * @param {Object} payload - Approval payload
 * @param {string} payload.approver_id - User ID of approver
 * @param {string} payload.approver_role - Role of approver (manager, owner, admin)
 * @param {string} [payload.approver_email] - Email for audit trail
 * @param {string} [payload.comment] - Optional comment
 * @returns {Promise<Object>} Updated approval with execution result
 */
export async function approveApproval(approvalId, payload) {
  const response = await fetch(`${BACKEND_URL}/api/blockchain/approvals/${approvalId}/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.detail || error.error || 'Failed to approve request');
  }
  
  return await response.json();
}

/**
 * Reject a pending request
 * @param {string} approvalId - Approval request ID
 * @param {Object} payload - Rejection payload
 * @param {string} payload.approver_id - User ID of rejector
 * @param {string} payload.approver_role - Role of rejector
 * @param {string} payload.reason - Rejection reason (required)
 * @param {string} [payload.approver_email] - Email for audit trail
 * @returns {Promise<Object>} Updated approval with rejection details
 */
export async function rejectApproval(approvalId, payload) {
  const response = await fetch(`${BACKEND_URL}/api/blockchain/approvals/${approvalId}/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.detail || error.error || 'Failed to reject request');
  }
  
  return await response.json();
}

/**
 * Get approval history for a hotel
 * @param {string} hotelId - Hotel identifier
 * @param {Object} params - Query parameters
 * @param {number} [params.limit=50] - Maximum number of records
 * @param {string} [params.status] - Filter by status (approved, rejected, expired)
 * @returns {Promise<Array>} List of approval history items
 */
export async function getApprovalHistory(hotelId, params = {}) {
  const url = new URL(`${BACKEND_URL}/api/blockchain/approvals/${hotelId}/history`);
  
  if (params.limit) {
    url.searchParams.append('limit', params.limit);
  }
  if (params.status) {
    url.searchParams.append('status', params.status);
  }
  
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch approval history: ${response.statusText}`);
  }
  
  return await response.json();
}
